// connect/connections.ts — what a MINTED connection is, and what "active" means.
//
// A connection is a named relationship with one tool. It is not the same thing
// as an OAuth token: tokens record that something was authorized, connections
// record WHO. Two Claude Codes on two machines are two connections, and the
// person names both, so "the studio one went quiet" is a sentence they can say.
//
// Pure module — no React, no I/O. Shared by the API route, the MCP endpoint, and
// the connections list.

import { CONNECT_TOOLS, MCP_URL } from './tools'
import type { ConnectionMemory } from './memory'

/** One minted connection, as the API returns it. */
export interface Connection {
  id: string
  /** catalogue id from CONNECT_TOOLS ('claude-code', 'chatgpt', 'other', …). */
  tool_id: string
  /** what the person called it. */
  label: string
  /** the attribution tag carried in this connection's endpoint. */
  token: string
  /** the oauth client that came through this door, once one has. */
  client_id: string | null
  created_at: string
  first_seen_at: string | null
  last_seen_at: string | null
  call_count: number
  /** set when innernet minted this connection on the person's behalf — a
   *  client that reached in without ever coming through a minted door
   *  (migration 098). Absent on a database where 098 has not landed. */
  adopted_at?: string | null
  /** what it remembers about the client on the other end — name, version,
   *  machine, door (lib/connect/memory.ts, migration 122). Server-written. */
  memory?: ConnectionMemory | null
}

// ── what "active" means ──────────────────────────────────────────────────────
//
// There is no way to ask a tool whether it is still connected — MCP has no
// heartbeat, and under the 2026-07-28 stateless core there is not even a session
// to observe: every call stands alone. So presence can only ever be a statement
// about RECENCY, and the honest thing is to say when a connection last read the
// memory rather than show a green light that implies someone is home.
//
// But recency has two useful resolutions, and collapsing them lost the one that
// matters most. Every call stamps `last_seen_at` (the MCP route's
// `connection_touch`), so a call that landed a minute ago is not the same fact
// as one that landed on Tuesday — the first means a tool is READING YOUR MEMORY
// RIGHT NOW, which is the thing a person opening this page actually wants to
// know, and it was being reported as the same flat "active" as a whole week of
// history.
//
//   live   — a call inside the last few minutes. Something is talking to
//            innernet as you read this.
//   active — read inside the week. In your life, just not this second.
//   quiet  — read, but not lately.
//   waiting— minted, never used. The endpoint has never been called.
//
// The week is long enough that a weekday tool never looks broken on a Monday,
// and short enough that something abandoned a month ago stops claiming to be
// part of your life. The live window is deliberately generous next to a single
// request: a tool mid-conversation goes quiet between turns, and blinking off
// after 30s would make a working connection look like a broken one.

export const ACTIVE_WINDOW_DAYS = 7
export const LIVE_WINDOW_MS = 3 * 60_000

export type ConnState = 'live' | 'active' | 'quiet' | 'waiting'

/** What a connection is doing, as of `now`. Pass a ticking `now` if the caller
 *  keeps this on screen — `live` decays with time alone, no data change. */
export function connState(c: Pick<Connection, 'last_seen_at'>, now = Date.now()): ConnState {
  if (!c.last_seen_at) return 'waiting'
  const age = now - new Date(c.last_seen_at).getTime()
  // a clock a little behind the server's can make a fresh stamp look future-dated;
  // that is still the freshest thing there is, so it counts as live.
  if (age <= LIVE_WINDOW_MS) return 'live'
  return age <= ACTIVE_WINDOW_DAYS * 86_400_000 ? 'active' : 'quiet'
}

/** Does this state mean the connection is part of the person's working life? */
export function isOn(state: ConnState): boolean {
  return state === 'live' || state === 'active'
}

/** The plain sentence a state deserves, given when it was last seen. */
export function stateLine(state: ConnState, rel: string | null): string {
  if (state === 'live') return 'connected — reading your memory now'
  if (state === 'active') return rel ? `active — last read ${rel} ago` : 'active'
  if (state === 'quiet') return rel ? `quiet since ${rel} ago` : 'quiet'
  return 'waiting for its first call'
}

// ── the door ─────────────────────────────────────────────────────────────────

/** The endpoint for ONE connection. Same MCP server, carrying the tag that says
 *  which relationship is speaking. The tag grants nothing — every call is still
 *  authorized by its own bearer token — so this url is safe to show and copy. */
export function connectionUrl(token: string, base = MCP_URL): string {
  // IN THE PATH, not the query (2026-09-02). Strict OAuth clients (ChatGPT)
  // derive the protected-resource metadata url from the resource url and then
  // require the document's `resource` to equal the url they were given. A
  // query string can never round-trip through that: the well-known path drops
  // it, so the document said `…/api/mcp` while the client held `…/api/mcp?c=…`
  // — and the connector failed right after sign-in. A path segment survives
  // both ways: `/.well-known/oauth-protected-resource/api/mcp/c/<tag>` answers
  // with exactly this url as its resource. The `?c=` form is still READ, so
  // every link already pasted into a tool keeps working.
  return `${base}/c/${encodeURIComponent(token)}`
}

const TAG = /^[A-Za-z0-9_-]{8,64}$/

/** Read the attribution tag off an incoming MCP request url — the path form
 *  (`/api/mcp/c/<tag>`) or the older query form (`?c=<tag>`). */
export function readTag(url: string | null | undefined): string | null {
  if (!url) return null
  try {
    const u = new URL(url, 'https://innernet.live')
    const m = u.pathname.match(/\/api\/mcp\/c\/([^/]+)\/?$/)
    const fromPath = m ? decodeURIComponent(m[1]) : null
    if (fromPath && TAG.test(fromPath)) return fromPath
    const t = u.searchParams.get('c')
    return t && TAG.test(t) ? t : null
  } catch {
    return null
  }
}

/** A short, unguessable, url-safe tag. Crypto random — this is an identifier
 *  that rides in a url a person may share a screenshot of, so it must not be
 *  guessable enough to let someone else's calls be attributed to them. */
export function mintToken(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

// ── naming ───────────────────────────────────────────────────────────────────

/** The name we OFFER when someone mints a connection: theirs, then the tool.
 *  Prefilled so the field is never blank, editable so it is never imposed. And
 *  when they already have one of these, the offer counts up rather than
 *  colliding — "john's claude code 2". */
export function suggestLabel(
  toolId: string,
  accountName: string | null | undefined,
  existing: Pick<Connection, 'tool_id' | 'label'>[] = [],
): string {
  const base = offeredLabel(toolId, accountName)
  const taken = new Set(existing.filter((e) => e.tool_id === toolId).map((e) => e.label.toLowerCase()))
  if (!taken.has(base.toLowerCase())) return base
  for (let n = 2; n < 50; n++) {
    const next = `${base} ${n}`
    if (!taken.has(next.toLowerCase())) return next
  }
  return base
}

/** The first word of the name a person goes by here — what the offered label
 *  is built from. The username they set wins everywhere; an email only ever
 *  stands in for an account that has not named itself. */
export function whoFor(accountName: string | null | undefined): string {
  return (accountName ?? '').trim().split(/[\s@]/)[0].toLowerCase()
}

/** The bare offer for one tool, before any "2" suffix: "john's claude code". */
export function offeredLabel(toolId: string, accountName: string | null | undefined): string {
  const tool = CONNECT_TOOLS.find((t) => t.id === toolId)
  const toolName = (tool?.generic ? 'connection' : tool?.name ?? toolId).toLowerCase()
  const who = whoFor(accountName)
  return who ? `${who}'s ${toolName}` : toolName
}

/** Was this label the one innernet OFFERED under `accountName` (with or
 *  without a counting suffix)? Returns the suffix so a rename can keep it, or
 *  null when the person wrote a name of their own. A label the person chose
 *  is theirs and is never touched. */
export function offeredSuffix(
  label: string,
  toolId: string,
  accountName: string | null | undefined,
): string | null {
  const base = offeredLabel(toolId, accountName).toLowerCase()
  const l = label.trim().toLowerCase()
  if (l === base) return ''
  if (l.startsWith(`${base} `)) {
    const rest = l.slice(base.length + 1)
    if (/^\d{1,2}$/.test(rest)) return ` ${rest}`
  }
  return null
}

/** When the person renames themselves, every connection still wearing the
 *  OLD offer follows the new name; anything they named by hand stays. Pure:
 *  returns the renames to apply. */
export function relabelForName(
  connections: Pick<Connection, 'id' | 'tool_id' | 'label'>[],
  oldName: string | null | undefined,
  newName: string | null | undefined,
): { id: string; label: string }[] {
  const oldWho = whoFor(oldName)
  const newWho = whoFor(newName)
  if (!oldWho || !newWho || oldWho === newWho) return []
  const out: { id: string; label: string }[] = []
  for (const c of connections) {
    const suffix = offeredSuffix(c.label, c.tool_id, oldName)
    if (suffix === null) continue
    out.push({ id: c.id, label: `${offeredLabel(c.tool_id, newName)}${suffix}` })
  }
  return out
}
